import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Flex,
  VStack,
  Input,
  Button,
  HStack,
  Text,
  Image,
  Spinner,
  useToast,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
} from '@chakra-ui/react';
import MessageTemplate, { VisualMetadata } from '../../pages/OrcanaLive/MessageTypes';
import orcanaLogo from '../../assets/images/orcana_logo.png';
import { motion, AnimatePresence } from 'framer-motion';
import Cookies from 'js-cookie';
import DOMPurify from 'dompurify';

const DATABASE_API_BASE = import.meta.env.VITE_BASE_DATABASE_URL;

export type Message = {
  message_id: number;
  inquiry_id: number;
  user_id: number;
  created_at: string;
  message_source: 'user' | 'orcana';
  body_original: string;
  body_rephrased: string | null;
  intent: string;
  analytics_run_id: number | null;
  feedback_type?: 'thumbs-up' | 'thumbs-down' | '';
  comments?: string | null;
  image?: string;
  message_metadata?: {
    image?: string;
    visualMetadata?: VisualMetadata | VisualMetadata[];
    hoverInfo?: { [key: string]: any };
    quickReplies?: string[];
  };
  visualMetadata?: VisualMetadata | VisualMetadata[];
};

interface ChatPanelProps {
  messages: Message[];
  messageDates: string[];
  input: string;
  setInput: (input: string) => void;
  handleSendMessage: (input: string, metadata?: { is_quick_reply?: boolean }) => Promise<void>;
  isLoading: boolean;
  isConversationSelected: boolean;
  isLoadingConversations: boolean;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  messageDates,
  input,
  setInput,
  handleSendMessage,
  isLoading,
  isConversationSelected,
  // isLoadingConversations,
}) => {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const token = Cookies.get('accessToken');

  const [hoveredMessageId, setHoveredMessageId] = useState<number | null>(null);
  const [selectedMessageForComments, setSelectedMessageForComments] = useState<Message | null>(null);
  const [commentText, setCommentText] = useState<string>('');
  const [feedback, setFeedback] = useState<{
    [messageId: number]: {
      thumbsUp?: boolean;
      thumbsDown?: boolean;
      comments?: string;
    };
  }>({});

  const sendLockRef = useRef(false);
  const guardedSend = useCallback(
    async (msg: string, md?: { is_quick_reply?: boolean }) => {
      if (sendLockRef.current || isLoading) return;
      sendLockRef.current = true;
      try {
        await handleSendMessage(msg, md);
      } finally {
        setTimeout(() => {
          sendLockRef.current = false;
        }, 500);
      }
    },
    [handleSendMessage, isLoading]
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const newFeedback: typeof feedback = {};
    messages.forEach((msg) => {
      const isThumbsUp = msg.feedback_type === 'thumbs-up';
      const isThumbsDown = msg.feedback_type === 'thumbs-down';
      const safeComment = msg.comments && msg.comments.trim() !== '' ? msg.comments : '';
      newFeedback[msg.message_id] = {
        thumbsUp: isThumbsUp,
        thumbsDown: isThumbsDown,
        comments: safeComment,
      };
    });
    setFeedback(newFeedback);
  }, [messages]);

  const handleHover = (messageId: number, source: 'user' | 'orcana') => {
    if (source === 'orcana') setHoveredMessageId(messageId);
  };
  const handleHoverEnd = () => setHoveredMessageId(null);

  const handleThumbsUp = async (msg: Message) => {
    try {
      const wasThumbsUp = !!feedback[msg.message_id]?.thumbsUp;

      setFeedback((prev) => ({
        ...prev,
        [msg.message_id]: {
          ...prev[msg.message_id],
          thumbsUp: !wasThumbsUp,
          thumbsDown: false,
        },
      }));

      console.log('Sending feedback request:', {
        messageId: msg.message_id,
        feedbackType: !wasThumbsUp ? 'thumbs-up' : '',
        comments: feedback[msg.message_id]?.comments || ' ',
      });

      const resp = await fetch(`${DATABASE_API_BASE}/update-message-feedback/${msg.message_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          feedback_type: wasThumbsUp ? '' : 'thumbs-up',
          comments: feedback[msg.message_id]?.comments || ' ',
        }),
        credentials: 'include',
      });

      console.log('Response status:', resp.status);
      const data = await resp.json();
      console.log('Response data:', data);

      if (!resp.ok) {
        throw new Error(`HTTP error! status: ${resp.status}`);
      }
      if (data.status !== 'success') {
        throw new Error(`API error: ${JSON.stringify(data)}`);
      }

      toast({
        title: !wasThumbsUp ? 'Feedback submitted' : 'Feedback removed',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Detailed error in handleThumbsUp:', {
        error,
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });

      toast({
        title: 'Failed to submit feedback',
        description: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleThumbsDown = async (msg: Message) => {
    try {
      const wasThumbsDown = !!feedback[msg.message_id]?.thumbsDown;

      setFeedback((prev) => ({
        ...prev,
        [msg.message_id]: {
          ...prev[msg.message_id],
          thumbsDown: !wasThumbsDown,
          thumbsUp: false,
        },
      }));

      if (!wasThumbsDown) {
        const resp = await fetch(`${DATABASE_API_BASE}/update-message-feedback/${msg.message_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            feedback_type: 'thumbs-down',
            comments: feedback[msg.message_id]?.comments || ' ',
          }),
        });

        if (!resp.ok) {
          throw new Error(`HTTP error! status: ${resp.status}`);
        }

        const data = await resp.json();
        if (data.status !== 'success') {
          throw new Error(`API error: ${JSON.stringify(data)}`);
        }

        toast({
          title: !wasThumbsDown ? 'Feedback submitted' : 'Feedback removed',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        const existingComment = feedback[msg.message_id]?.comments || '';
        const safeComment = existingComment.trim() === '' ? ' ' : existingComment;

        const resp = await fetch(`${DATABASE_API_BASE}/update-message-feedback/${msg.message_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            feedback_type: '',
            comments: safeComment,
          }),
        });
        const data = await resp.json();
        if (data.status === 'success') {
          toast({
            title: 'Feedback removed',
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        } else {
          throw new Error('API call failed');
        }
      }
    } catch (error) {
      console.error('Detailed error in handleThumbsDown:', {
        error,
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });

      toast({
        title: 'Failed to submit feedback',
        description: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleOpenComments = (msg: Message) => {
    setSelectedMessageForComments(msg);
    setCommentText(feedback[msg.message_id]?.comments || '');
    onOpen();
  };

  const handleSaveComment = async () => {
    if (!selectedMessageForComments) return;

    const msgId = selectedMessageForComments.message_id;
    const previousComment = feedback[msgId]?.comments || '';

    setFeedback((prev) => ({
      ...prev,
      [msgId]: {
        ...prev[msgId],
        comments: commentText,
      },
    }));

    try {
      const isUp = feedback[msgId]?.thumbsUp;
      const isDown = feedback[msgId]?.thumbsDown;
      const newFeedbackType = isUp ? 'thumbs-up' : isDown ? 'thumbs-down' : '';

      const safeComments =
        commentText.trim() === '' && newFeedbackType === ''
          ? ' '
          : commentText;

      const resp = await fetch(`${DATABASE_API_BASE}/update-message-feedback/${msgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          feedback_type: newFeedbackType,
          comments: safeComments,
        }),
      });
      const data = await resp.json();
      if (data.status !== 'success') {
        throw new Error('API call failed');
      }

      toast({
        title: 'Comment saved',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving comment:', error);
      setFeedback((prev) => ({
        ...prev,
        [msgId]: {
          ...prev[msgId],
          comments: previousComment,
        },
      }));

      toast({
        title: 'Failed to submit feedback',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
    onClose();
  };

  const handleDeleteComment = async () => {
    if (!selectedMessageForComments) return;

    try {
      const msgId = selectedMessageForComments.message_id;
      const currentThumbsUp = feedback[msgId]?.thumbsUp;
      const currentThumbsDown = feedback[msgId]?.thumbsDown;

      const safeComment = currentThumbsUp || currentThumbsDown ? '' : ' ';

      setFeedback((prev) => ({
        ...prev,
        [msgId]: {
          ...prev[msgId],
          comments: '',
        },
      }));

      const response = await fetch(
        `${DATABASE_API_BASE}/update-message-feedback/${msgId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            feedback_type: currentThumbsUp
              ? 'thumbs-up'
              : currentThumbsDown
                ? 'thumbs-down'
                : '',
            comments: safeComment,
          }),
        }
      );

      const data = await response.json();
      if (data.status === 'success') {
        toast({
          title: 'Comment deleted',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        throw new Error('API call failed');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        title: 'Failed to delete comment. Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }

    onClose();
  };

   return (
    <Flex flexDirection="column" w="100%" h="100%">
      <Box
        flex="9"
        overflowY="auto"
        overflowX="hidden"
        pr={4}
        pb={1}
        bg="gray.50"
        boxShadow="lg"
        css={{
          '&::-webkit-scrollbar': { width: '0px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '24px' },
          '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '24px', '&:hover': { background: '#a0aec0' } },
        }}
      >
        {!isConversationSelected ? (
          <Flex height="100%" alignItems="center" justifyContent="center" flexDirection="column" p={4}>
            <Image src={orcanaLogo} alt="Orcana Logo" width="150px" mb={6} opacity={0.8} />
            <Text color="gray.500" fontSize="lg" textAlign="center" maxW="400px">
              Type to start a new conversation!
            </Text>
          </Flex>
        ) : (
          <VStack w="100%" spacing={2} py={2} px={4} align="stretch">
            {messages.map((msg, index) => {
              const messageWithImage: Message = {
                ...msg,
                image: msg.image || (msg.message_metadata?.image ?? ''),
              };
              return (
                <React.Fragment key={msg.message_id}>
                  {messageDates[index] !== messageDates[index - 1] && (
                    <Text textAlign="center" fontSize="22px" fontWeight="bold" color="#4a6e8c" my={2}>
                      {messageDates[index]}
                    </Text>
                  )}
                  <MessageTemplate
                    message={messageWithImage}
                    showHoverPanel={hoveredMessageId === msg.message_id && msg.message_source === 'orcana'}
                    isHovered={hoveredMessageId === msg.message_id}
                    onMouseEnter={() => handleHover(msg.message_id, msg.message_source)}
                    onMouseLeave={handleHoverEnd}
                    thumbsUp={feedback[msg.message_id]?.thumbsUp || false}
                    thumbsDown={feedback[msg.message_id]?.thumbsDown || false}
                    comments={feedback[msg.message_id]?.comments || ''}
                    onThumbsUp={() => handleThumbsUp(msg)}
                    onThumbsDown={() => handleThumbsDown(msg)}
                    onOpenComments={() => handleOpenComments(msg)}
                  />
                </React.Fragment>
              );
            })}
            {isLoading && (
              <Box textAlign="left" mt="8px">
                <Spinner size="md" />
              </Box>
            )}
            <div ref={messagesEndRef} />
          </VStack>
        )}
      </Box>

      {/* Quick Replies carousel */}
      <AnimatePresence>
        {(() => {
          const latestQR = [...messages]
            .reverse()
            .find((m) => m.message_source === 'orcana' && m.message_metadata?.quickReplies?.length)
            ?.message_metadata!.quickReplies!;
          if (!latestQR || latestQR.length === 0) return null;
          return (
            <Box p={4} mb={0} pointerEvents={isLoading ? 'none' : 'auto'} opacity={isLoading ? 0.5 : 1}>
              <Flex align="center" wrap="wrap" gap={4}>
                <Text fontSize="md" fontWeight="semibold" color="accent.500" w="100px">
                  Suggested Follow Ups
                </Text>
                <Flex flex="1" wrap="wrap" gap={3}>
                  {latestQR.map((text) => (
                    <motion.div
                      key={text}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    >
                      <Button
                        size="sm"
                        variant="outline"
                        borderRadius="3xl"
                        px={4}
                        py={2}
                        whiteSpace="normal"
                        wordBreak="break-word"
                        onClick={() => guardedSend(text, { is_quick_reply: false })}
                        isDisabled={isLoading}
                        color="primary.500"
                        _hover={{ transform: 'scale(1.02)', bg: 'gray.100', color: 'accent.500' }}
                      >
                        {text}
                      </Button>
                    </motion.div>
                  ))}
                </Flex>
              </Flex>
            </Box>
          );
        })()}
      </AnimatePresence>

      {/* Input & Send */}
      <Flex
        flex="1"
        p={3}
        bg="gray.100"
        borderTop="2px solid"
        borderColor="gray.100"
        alignItems="center"
        justifyContent="center"
        boxShadow="0 -2px 5px rgba(0, 0, 0, 0.1), 2px 0 5px rgba(0, 0, 0, 0.1), -2px 0 5px rgba(0, 0, 0, 0.1)"
      >
        <HStack spacing={2} w="100%" mr={4}>
          <Input
            placeholder="Ask Orcana anything…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                guardedSend(input);
              }
            }}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
            fontSize="lg"
          />
          <Button
            colorScheme="primary"
            onClick={() => guardedSend(input)}
            isDisabled={isLoading}
            isLoading={isLoading}
            loadingText="Sending…"
            borderRadius="lg"
            transition="transform 0.2s"
            _hover={{ transform: 'scale(1.05)' }}
          >
            Send
          </Button>
        </HStack>
      </Flex>

      {/* Comment side drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay>
          <DrawerContent>
            <DrawerHeader>Bot Message Comments</DrawerHeader>
            <DrawerBody>
              {selectedMessageForComments && (
                <Box mb={4}>
                  <Box
                    fontSize="sm"
                    fontWeight="medium"
                    p={4}
                    borderRadius="lg"
                    bg="gray.100"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedMessageForComments.body_original),
                    }}
                  />
                </Box>
              )}
              <Text mb={2} fontWeight="bold">
                Comments:
              </Text>
              <Input
                placeholder="Enter your comments here..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveComment();
                  }
                }}
              />
            </DrawerBody>
            <DrawerFooter>
              <Button variant="outline" mr={3} onClick={handleDeleteComment}>
                Delete Comment
              </Button>
              <Button variant="outline" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleSaveComment}>
                Save
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    </Flex>
  );
};

export default ChatPanel;